/**
 * What happened to one WhatsApp message after we sent it:
 * Sent → Delivered → Read → Replied.
 *
 * The delivery's own status tag only answers "did our send succeed?", so it
 * stays SENT while the gateway reports delivery and reading. Those receipts are
 * stored on the delivery's `whatsapp` block, and replies on `replies[]` — this is
 * where they are shown.
 *
 * A step with no time is not a failure. Receipts reach us through the WhatsApp
 * webhook or the reconcile background job; if neither is running, a message
 * that was read stays at Sent here, and the note under the steps says so.
 */
import { useState } from 'react';
import type { DeliveryReply, DeliveryWhatsapp } from '../../services/notification-runs.service';

const fmt = (iso?: string | null) => (iso ? new Date(iso).toLocaleString() : '');

type StepState = 'done' | 'pending' | 'failed';

const Step = ({ label, at, state, last }: {
  label: string; at?: string | null; state: StepState; last?: boolean;
}) => {
  const icon = state === 'done'
    ? 'pi-check-circle text-emerald-600 dark:text-emerald-400'
    : state === 'failed'
      ? 'pi-times-circle text-rose-600 dark:text-rose-400'
      : 'pi-circle text-gray-300 dark:text-gray-600';
  return (
    <div className="flex items-center gap-1.5">
      <i className={`pi ${icon} text-sm`} />
      <div className="leading-tight">
        <div className={`text-xs ${state === 'pending' ? 'text-gray-400' : 'font-medium text-ink'}`}>
          {label}
        </div>
        {at ? <div className="text-[10px] text-ink-muted">{fmt(at)}</div> : null}
      </div>
      {!last && <i className="pi pi-angle-right mx-1 text-xs text-gray-300 dark:text-gray-600" />}
    </div>
  );
};

/** A recency match is a guess from the phone number; worth saying so beside the words. */
const isGuess = (matchedBy?: string | null) => /recen|phone|digit/i.test(String(matchedBy || ''));

const WhatsAppProgress = ({ whatsapp, replies }: {
  whatsapp: DeliveryWhatsapp;
  replies?: DeliveryReply[] | null;
}) => {
  const [showReplies, setShowReplies] = useState(false);

  const status = String(whatsapp.status || '').toUpperCase();
  const failed = status === 'FAILED';
  const thread = replies || [];
  const replyCount = Math.max(whatsapp.replyCount || 0, thread.length);

  const sentState: StepState = failed ? 'failed' : whatsapp.sentAt || status ? 'done' : 'pending';
  const noReceiptYet = !failed && sentState === 'done' && !whatsapp.deliveredAt && !whatsapp.readAt && replyCount === 0;

  return (
    <div className="flex flex-col gap-1.5 rounded border border-emerald-100 bg-emerald-50/40 p-2 dark:border-emerald-900/40 dark:bg-emerald-950/10"
      data-testid="WhatsAppProgress">
      <div className="flex flex-wrap items-center gap-y-1">
        <i className="pi pi-whatsapp mr-2 text-sm text-emerald-600 dark:text-emerald-400" />
        <Step label={status === 'QUEUED' ? 'Queued' : failed ? 'Failed' : 'Sent'} at={whatsapp.sentAt} state={sentState} />
        <Step label="Delivered" at={whatsapp.deliveredAt} state={whatsapp.deliveredAt ? 'done' : 'pending'} />
        <Step label="Read" at={whatsapp.readAt} state={whatsapp.readAt ? 'done' : 'pending'} />
        <Step label={replyCount ? `Replied (${replyCount})` : 'Replied'} at={whatsapp.lastReplyAt}
          state={replyCount ? 'done' : 'pending'} last />
      </div>

      {noReceiptYet && (
        <span className="text-[11px] text-ink-muted">
          No receipt from the gateway yet. Delivered, read and replies arrive through the WhatsApp
          webhook  — if it is not running, this stays at Sent.
        </span>
      )}

      {/* The ids receipts and replies are matched on — what to compare against the gateway's logs. */}
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 font-mono text-[10px] text-gray-400">
        {whatsapp.toDigits && <span>chat {whatsapp.toDigits}</span>}
       
        {whatsapp.ackProviderId && <span>msg {whatsapp.ackProviderId}</span>}
        {whatsapp.providerMessageId != null && <span>{whatsapp.provider || 'gateway'} </span>}
      </div>

      {thread.length > 0 && (
        <div>
          <button type="button" onClick={() => setShowReplies((v) => !v)}
            className="text-xs font-medium text-blue-600 hover:underline dark:text-blue-400">
            {showReplies ? 'Hide replies' : `Show ${thread.length} ${thread.length === 1 ? 'reply' : 'replies'}`}
          </button>
          {showReplies && (
            <ul className="mt-1.5 flex flex-col gap-1.5">
              {thread.map((r, i) => (
                <li key={r.providerMessageId || i}
                  className="rounded border border-gray-200 bg-white p-2 dark:border-gray-700 dark:bg-gray-800">
                  <div className="mb-0.5 flex flex-wrap items-center gap-2 text-[11px] text-ink-muted">
                    <span className="font-medium text-ink">{r.senderName || r.fromDigits || r.from || 'Recipient'}</span>
                    {r.at && <span>{fmt(r.at)}</span>}
                    {r.matchedBy && (
                      <span className={isGuess(r.matchedBy) ? 'text-amber-600 dark:text-amber-400' : ''}
                        title={isGuess(r.matchedBy) ? 'Matched by phone number and time, not an exact message id' : undefined}>
                        matched by {r.matchedBy}
                      </span>
                    )}
                  </div>
                  {r.body
                    ? <p className="whitespace-pre-wrap break-words text-xs text-gray-700 dark:text-gray-300">{r.body}</p>
                    : <span className="text-xs text-gray-400">({r.messageType || 'message'} with no text)</span>}
                  {r.mediaUrl && (
                    <a href={r.mediaUrl} target="_blank" rel="noreferrer"
                      className="text-[11px] text-blue-600 hover:underline dark:text-blue-400">Open attachment</a>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default WhatsAppProgress;
